import { useState, useEffect, useCallback, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Upload, Search, Pencil, Save, X, Trash2, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";

export default function BorderMasterPage() {
  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [dragging, setDragging] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ border_type: "", bobbin_ends: "", cost_per_end_1000mtrs: "", jari_cost_per_end_1000mtrs: "", kuri_weight: "" });
  const [addLoading, setAddLoading] = useState(false);
  const fileRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get("/border-master");
      setData(res.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleUpload = async (file) => {
    if (!file || !file.name.endsWith(".csv")) {
      toast.error("Please upload a CSV file");
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await api.post("/border-master/upload-csv", formData);
      toast.success(res.data.message);
      fetchData();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    handleUpload(e.dataTransfer.files[0]);
  };

  const handleAdd = async () => {
    if (!addForm.border_type) { toast.error("Border Type is required"); return; }
    setAddLoading(true);
    try {
      await api.post("/border-master", {
        border_type: addForm.border_type, bobbin_ends: parseInt(addForm.bobbin_ends) || 0,
        cost_per_end_1000mtrs: parseFloat(addForm.cost_per_end_1000mtrs) || 0,
        jari_cost_per_end_1000mtrs: parseFloat(addForm.jari_cost_per_end_1000mtrs) || 0,
        kuri_weight: parseFloat(addForm.kuri_weight) || 0,
      });
      toast.success("Border added"); setAddOpen(false);
      setAddForm({ border_type: "", bobbin_ends: "", cost_per_end_1000mtrs: "", jari_cost_per_end_1000mtrs: "", kuri_weight: "" });
      fetchData();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setAddLoading(false); }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditValues({
      border_type: item.border_type,
      bobbin_ends: item.bobbin_ends,
      cost_per_end_1000mtrs: item.cost_per_end_1000mtrs,
      jari_cost_per_end_1000mtrs: item.jari_cost_per_end_1000mtrs || 0,
      kuri_weight: item.kuri_weight,
    });
  };

  const saveEdit = async (id) => {
    try {
      await api.put(`/border-master/${id}`, editValues);
      toast.success("Border data updated");
      setEditingId(null);
      fetchData();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteItem = async (id) => {
    try {
      await api.delete(`/border-master/${id}`);
      toast.success("Item deleted");
      fetchData();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const filtered = data.filter((item) =>
    item.border_type?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="border-master-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-stone-900 tracking-tight">Border Master</h1>
          <p className="text-sm text-stone-500 mt-1">Manage border types. Upload CSV to merge/update or add manually.</p>
        </div>
        <Button data-testid="add-border-button" className="bg-[#C2410C] hover:bg-[#9A3412] text-white" onClick={() => setAddOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />Add Border
        </Button>
      </div>

      {/* CSV Upload */}
      <div
        className={`csv-dropzone ${dragging ? "dragging" : ""}`}
        data-testid="border-csv-upload-zone"
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        onClick={() => fileRef.current?.click()}
      >
        <input
          ref={fileRef}
          type="file"
          accept=".csv"
          className="hidden"
          data-testid="border-csv-file-input"
          onChange={(e) => handleUpload(e.target.files[0])}
        />
        {uploading ? (
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-[#C2410C]" />
        ) : (
          <>
            <Upload className="h-8 w-8 mx-auto text-stone-400 mb-2" strokeWidth={1.5} />
            <p className="text-sm font-medium text-stone-700">Drop CSV to merge/update existing data</p>
            <p className="text-xs text-stone-400 mt-1">Columns: Border_Type, Bobbin_Ends, Cost_Per_End_1000Mtrs, Jari_Cost_Per_End_1000Mtrs, Kuri_Weight</p>
          </>
        )}
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
        <Input
          data-testid="border-search-input"
          placeholder="Search by border type..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9 h-10"
        />
      </div>

      {/* Table */}
      <div className="bg-white border border-stone-200 rounded-lg shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-stone-50">
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-stone-500 px-4 py-3">Border Type</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-stone-500 px-4 py-3 text-right">Bobbin Ends</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-stone-500 px-4 py-3 text-right">Polyester Cost/End</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-stone-500 px-4 py-3 text-right">Jari Cost/End</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-stone-500 px-4 py-3 text-right">Kuri Weight</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-stone-500 px-4 py-3 w-28">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin mx-auto text-stone-400" />
                </TableCell>
              </TableRow>
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-12 text-stone-400">
                  {data.length === 0 ? "No data. Upload a CSV to get started." : "No matching records."}
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((item, idx) => (
                <TableRow
                  key={item.id}
                  className="animate-row hover:bg-stone-50/50"
                  style={{ animationDelay: `${idx * 30}ms` }}
                  data-testid={`border-row-${item.id}`}
                >
                  <TableCell className="px-4 py-2.5 text-sm font-medium text-stone-900">
                    {editingId === item.id ? (
                      <Input value={editValues.border_type} onChange={(e) => setEditValues((v) => ({ ...v, border_type: e.target.value }))} className="h-8 w-40" />
                    ) : (
                      item.border_type
                    )}
                  </TableCell>
                  <TableCell className="px-4 py-2.5 text-sm text-right">
                    {editingId === item.id ? (
                      <Input type="number" value={editValues.bobbin_ends} onChange={(e) => setEditValues((v) => ({ ...v, bobbin_ends: parseInt(e.target.value) || 0 }))} className="h-8 w-24 text-right ml-auto" />
                    ) : (
                      <span className="font-mono">{item.bobbin_ends}</span>
                    )}
                  </TableCell>
                  <TableCell className="px-4 py-2.5 text-sm text-right">
                    {editingId === item.id ? (
                      <Input type="number" step="0.001" value={editValues.cost_per_end_1000mtrs} onChange={(e) => setEditValues((v) => ({ ...v, cost_per_end_1000mtrs: parseFloat(e.target.value) || 0 }))} className="h-8 w-28 text-right ml-auto" />
                    ) : (
                      <span className="font-mono">{item.cost_per_end_1000mtrs?.toFixed(4)}</span>
                    )}
                  </TableCell>
                  <TableCell className="px-4 py-2.5 text-sm text-right">
                    {editingId === item.id ? (
                      <Input type="number" step="0.001" value={editValues.jari_cost_per_end_1000mtrs} onChange={(e) => setEditValues((v) => ({ ...v, jari_cost_per_end_1000mtrs: parseFloat(e.target.value) || 0 }))} className="h-8 w-28 text-right ml-auto" />
                    ) : (
                      <span className="font-mono">{(item.jari_cost_per_end_1000mtrs || 0)?.toFixed(4)}</span>
                    )}
                  </TableCell>
                  <TableCell className="px-4 py-2.5 text-sm text-right">
                    {editingId === item.id ? (
                      <Input type="number" step="0.001" value={editValues.kuri_weight} onChange={(e) => setEditValues((v) => ({ ...v, kuri_weight: parseFloat(e.target.value) || 0 }))} className="h-8 w-24 text-right ml-auto" />
                    ) : (
                      <span className="font-mono">{item.kuri_weight?.toFixed(4)}</span>
                    )}
                  </TableCell>
                  <TableCell className="px-4 py-2.5">
                    {editingId === item.id ? (
                      <div className="flex gap-1">
                        <Button size="icon" variant="ghost" data-testid="save-border-button" onClick={() => saveEdit(item.id)} className="h-7 w-7 text-green-600 hover:bg-green-50">
                          <Save className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => setEditingId(null)} className="h-7 w-7 text-stone-500 hover:bg-stone-100">
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex gap-1">
                        <Button size="icon" variant="ghost" data-testid="edit-border-button" onClick={() => startEdit(item)} className="h-7 w-7 text-stone-500 hover:text-[#C2410C] hover:bg-orange-50">
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="icon" variant="ghost" data-testid="delete-border-button" onClick={() => deleteItem(item.id)} className="h-7 w-7 text-stone-400 hover:text-red-600 hover:bg-red-50">
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {data.length > 0 && (
        <p className="text-xs text-stone-400">Showing {filtered.length} of {data.length} records</p>
      )}

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-heading">Add Border</DialogTitle><DialogDescription>Enter border type details.</DialogDescription></DialogHeader>
          <div className="space-y-3 mt-2">
            <div><Label className="text-sm font-medium mb-1.5 block">Border Type *</Label><Input placeholder="e.g. Katchi Karai" value={addForm.border_type} onChange={(e) => setAddForm((f) => ({ ...f, border_type: e.target.value }))} /></div>
            <div><Label className="text-sm font-medium mb-1.5 block">Bobbin Ends</Label><Input type="number" value={addForm.bobbin_ends} onChange={(e) => setAddForm((f) => ({ ...f, bobbin_ends: e.target.value }))} /></div>
            <div><Label className="text-sm font-medium mb-1.5 block">Polyester Cost/End</Label><Input type="number" step="0.001" value={addForm.cost_per_end_1000mtrs} onChange={(e) => setAddForm((f) => ({ ...f, cost_per_end_1000mtrs: e.target.value }))} /></div>
            <div><Label className="text-sm font-medium mb-1.5 block">Jari Cost/End</Label><Input type="number" step="0.001" value={addForm.jari_cost_per_end_1000mtrs} onChange={(e) => setAddForm((f) => ({ ...f, jari_cost_per_end_1000mtrs: e.target.value }))} /></div>
            <div><Label className="text-sm font-medium mb-1.5 block">Kuri Weight</Label><Input type="number" step="0.001" value={addForm.kuri_weight} onChange={(e) => setAddForm((f) => ({ ...f, kuri_weight: e.target.value }))} /></div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button className="bg-[#C2410C] hover:bg-[#9A3412] text-white" onClick={handleAdd} disabled={addLoading}>
              {addLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}{addLoading ? "Adding..." : "Add Border"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
